import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { HomeType } from "../home.constants";
import { SessionStateService } from "../../../core/services/session-state.service";
import { DevicesService } from "../../devices/devices.service";
import {
  DeviceType,
  STATE_VAR,
  GROUP,
  CONNECTIVITY_STATE,
} from "../../devices/devices.constant";
import { RealTimeMessage } from "../../devices/real-time-message.model";
import { HOUSE_PROFILE } from "../../houses/houses.constants";
import { IotService } from "../../../core/services/iot.service";
import { Message } from "@angular/compiler/src/i18n/i18n_ast";
import { Observable } from "rxjs/Observable";
import "rxjs/add/observable/empty";
import { empty } from "rxjs/observable/empty";
import { Subscription } from "rxjs";
import { Device } from "../../devices/devices.model_new";
import { EnergyService } from "../../energy/energy.service";
import { GeneralService } from "../../../../app/shared/services/general.service";
import { MatDialog, MatDialogRef } from "@angular/material";
import { MessageDialogComponent } from "../../../shared/components/message-dialog/message-dialog.component";
import { of } from "rxjs/observable/of";

@Component({
  selector: "app-home-tab",
  templateUrl: "./home-tab.component.html",
  styleUrls: ["./home-tab.component.scss"],
})
export class HomeTabComponent implements OnInit, OnDestroy {
  HomeType = HomeType;

  // Used for houses where installation was not made yet
  dialogRef: MatDialogRef<MessageDialogComponent>;

  /** Consumption */
  smartMeterIDs: Map<string, number> = new Map<string, number>();

  /** MicroGeneration */
  consumptionMeterIDs: Map<string, number> = new Map<string, number>();
  productionMeterIDs: Map<string, number> = new Map<string, number>();
  gridConsumption = null;

  productionIDs = [];
  productionValues = new Map<string, number>();

  gridInjection = null;
  selfConsumption = null;

  realTimeMessage: any;
  realTimeSubscription: Subscription;

  atLeastOneProductionModuleIsOnline: boolean;
  consumptionSmartMeterIsOnline: boolean;
  injectionSmartMeterIsOnline: boolean;

  @Input()
  defaultTab: string;
  @Input()
  tabs: string[];
  @Input()
  deviceIsNotInstalledYet: boolean;
  tab: string;

  @Output()
  change: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    private route: ActivatedRoute,
    private sessionStateService: SessionStateService,
    private devicesService: DevicesService,
    private iotService: IotService,
    private energyService: EnergyService,
    private generalService: GeneralService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe((values) => {
      const hasTab = values.homeType && this.tabs.includes(values.homeType);
      this.tab =
        values.homeType && this.tabs.includes(values.homeType)
          ? values.homeType
          : this.defaultTab;
      if (this.tab !== this.defaultTab) {
        this.change.emit(hasTab);
      }
    });

    // For houses where the installation was not yet made, the process ends here
    if (this.deviceIsNotInstalledYet) {
      this.consumptionSmartMeterIsOnline = false;
      this.atLeastOneProductionModuleIsOnline = false;
      this.injectionSmartMeterIsOnline = false;
      setTimeout(() => {
        this.dialogRef = this.dialog.open(MessageDialogComponent, {
          panelClass: "message-dialog",
          data: {
            title: this.generalService.translate.instant(
              "Welcome to EDP Re:dy app title"
            ),
            message: this.generalService.translate.instant(
              "Welcome to EDP Re:dy app message"
            ),
          },
          autoFocus: true,
          disableClose: true,
        });
      }, 3000);
      return;
    }

    const houseId = this.sessionStateService.getHouse().houseId;
    this.devicesService
      .getSmartMeter(houseId)
      .map((data) => {
        const houseProfile = this.sessionStateService.getHouse().houseProfile;
        if (houseProfile === HOUSE_PROFILE.CONSUMPTION) {
          this.smartMeterIDs = this.findConsumptionSmartMeters(data);
          this.consumptionSmartMeterIsOnline = this.validateConnectivityStateConsumption(
            data
          );
        } else if (houseProfile === HOUSE_PROFILE.STORAGE) {
          this.smartMeterIDs = this.findConsumptionSmartMeters(data);
          this.productionMeterIDs = this.findInjectionMeters(data);
          this.consumptionSmartMeterIsOnline = this.validateConnectivityStateConsumption(
            data
          );
        } else if (
          houseProfile === HOUSE_PROFILE.MICROGENERATION ||
          houseProfile === HOUSE_PROFILE.COMMUNITY_PRODUCTION
        ) {
          this.consumptionMeterIDs = this.findConsumptionSmartMeters(data);
          this.productionMeterIDs = this.findMicroInjectionMeters(data);
          this.consumptionSmartMeterIsOnline = this.validateConnectivityStateConsumption(
            data
          );
          this.injectionSmartMeterIsOnline = this.validateConnectivityStateInjection(
            data
          );
        } else if (
          houseProfile === HOUSE_PROFILE.SELF_CONSUMPTION ||
          houseProfile === HOUSE_PROFILE.COMMUNITY_CONSUMPTION ||
          houseProfile === HOUSE_PROFILE.COMMUNITY_SELFCONSUMPTION
        ) {
          this.smartMeterIDs = this.findConsumptionSmartMeters(data);
          this.productionMeterIDs = this.findInjectionMeters(data);
          this.consumptionSmartMeterIsOnline = this.validateConnectivityStateConsumption(
            data
          );
        }
      })
      .catch((err) => {
        // If an error happened obtaining smartmeter, then it should be considered offline
        this.consumptionSmartMeterIsOnline = false;
        return Observable.empty();
      })
      .flatMap(() => {
        if (
          this.sessionStateService.getHouse().houseProfile ===
            HOUSE_PROFILE.SELF_CONSUMPTION ||
          this.sessionStateService.getHouse().houseProfile ===
            HOUSE_PROFILE.STORAGE ||
          this.sessionStateService.getHouse().houseProfile ===
            HOUSE_PROFILE.COMMUNITY_CONSUMPTION ||
          this.sessionStateService.getHouse().houseProfile ===
            HOUSE_PROFILE.COMMUNITY_SELFCONSUMPTION ||
          this.sessionStateService.getHouse().houseProfile ===
            HOUSE_PROFILE.COMMUNITY_PRODUCTION
        ) {
          return this.devicesService
            .getHouseModules(houseId, DeviceType.GENERATION)
            .map((data) => {
              data.forEach((d) =>
                this.productionIDs.push(d.getModuleLocalId())
              );
              this.atLeastOneProductionModuleIsOnline = this.validateConnectivityStateProduction(
                data
              );
              return this.productionIDs;
            })
            .catch((err) => {
              this.atLeastOneProductionModuleIsOnline = false;
              return of(empty);
            });
        } else {
          return Observable.empty();
        }
      })
      .subscribe(undefined, undefined, () => {
        this.iotService.connect();
        this.obtainRealTimeResponses();
      });
  }

  findConsumptionSmartMeters(meters: Device[]) {
    const consumptionMeters: Map<string, number> = new Map<string, number>();
    meters.forEach((meter) => {
      if (meter.getGroups().has(GROUP.CONSUMPTION_METER)) {
        consumptionMeters.set(meter.getModuleLocalId(), null);
      }
    });
    return consumptionMeters;
  }

  findInjectionMeters(meters: Device[]) {
    const injectionMeters: Map<string, number> = new Map<string, number>();
    meters.forEach((meter) => {
      if (meter.getGroups().has(GROUP.INJECTION_METER)) {
        injectionMeters.set(meter.getModuleLocalId(), null);
      }
    });
    return injectionMeters;
  }

  findMicroInjectionMeters(meters: Device[]) {
    const injectionMeters: Map<string, number> = new Map<string, number>();
    meters.forEach((meter) => {
      if (
        meter.getGroups().has(GROUP.INJECTION_METER) &&
        !meter.getGroups().has(GROUP.CONSUMPTION_METER)
      ) {
        injectionMeters.set(meter.getModuleLocalId(), null);
      }
    });
    return injectionMeters;
  }

  // Returns true if at least 1 production module is online
  private validateConnectivityStateProduction(
    productionModules: Device[]
  ): boolean {
    return productionModules.some(
      (productionModule) =>
        productionModule.getConnectivityState() === CONNECTIVITY_STATE.ONLINE
    );
  }

  // Returns true if at least 1 consumption smartMeter module is online
  private validateConnectivityStateConsumption(
    allConsumptionModules: Device[]
  ): boolean {
    const smartMeterIDs = this.smartMeterIDs;
    const consumptionMeterIDs = this.consumptionMeterIDs;
    let consumptionSmartMeters = allConsumptionModules.filter(
      (consumptionModule) =>
        smartMeterIDs.has(consumptionModule.getModuleLocalId()) ||
        consumptionMeterIDs.has(consumptionModule.getModuleLocalId())
    );
    return consumptionSmartMeters.some(
      (consumptionSmartMeter) =>
        consumptionSmartMeter.getConnectivityState() ===
        CONNECTIVITY_STATE.ONLINE
    );
  }

  // Returns true if at least 1 injection smartMeter module is online
  private validateConnectivityStateInjection(
    allConsumptionModules: Device[]
  ): boolean {
    const productionMeterIDs = this.productionMeterIDs;
    let injectionSmartMeters = allConsumptionModules.filter(
      (consumptionModule) =>
        productionMeterIDs.has(consumptionModule.getModuleLocalId())
    );
    return injectionSmartMeters.some(
      (injectionSmartMeter) =>
        injectionSmartMeter.getConnectivityState() === CONNECTIVITY_STATE.ONLINE
    );
  }

  private obtainRealTimeResponses(): void {
    this.obtainCachedResponses();
    if (this.iotService.messagesObserver) {
      this.realTimeSubscription = this.iotService.messagesObserver.subscribe(
        (message) => {
          this.processMessage(message, false);
        }
      );
    }
  }

  private obtainCachedResponses(): void {
    const toSendConsMsgs = [];
    this.iotService.responseHistory.forEach((message, moduleLocalId) => {
      this.processMessage(message, true);
    });
  }

  private processMessage(message: any, isCache: boolean): void {
    // console.log(message);
    this.realTimeMessage = message;
    const houseProfile = this.sessionStateService.getHouse().houseProfile;
    /**Consumption && Storage **/
    if (houseProfile === "Consumption") {
      this.applyingValues(message);
      /** Storage */
    } else if (houseProfile === "Storage") {
      if (
        Array.from(this.smartMeterIDs.keys()).includes(message.moduleLocalId)
      ) {
        this.applyingValues(message);
      }
      // Injection
      if (
        Array.from(this.productionMeterIDs.keys()).includes(
          message.moduleLocalId
        )
      ) {
        this.applyingInjectionValues(message);
      }
      // Production
      if (this.productionIDs.includes(message.moduleLocalId)) {
        this.applyingProductionValues(message);
      }
      /** Micro */
    } else if (houseProfile === "Microgeneration") {
      // Consumption
      if (
        Array.from(this.consumptionMeterIDs.keys()).includes(
          message.moduleLocalId
        )
      ) {
        this.applyingValues(message);
      }
      // Generation
      if (
        Array.from(this.productionMeterIDs.keys()).includes(
          message.moduleLocalId
        )
      ) {
        this.applyingInjectionValues(message);
      }
    } else if (houseProfile === "Selfconsumption") {
      // Consumption
      if (
        Array.from(this.smartMeterIDs.keys()).includes(message.moduleLocalId)
      ) {
        this.applyingValues(message);
      }
      // Injection
      if (
        Array.from(this.productionMeterIDs.keys()).includes(
          message.moduleLocalId
        )
      ) {
        this.applyingInjectionValues(message);
      }
      // Production
      if (this.productionIDs.includes(message.moduleLocalId)) {
        this.applyingProductionValues(message);
      }
    }
  }

  private applyingValues(message: any): void {
    const platformKey = message.platformKey;
    if (platformKey === STATE_VAR.ACTIVE_POWER_A_PLUS) {
      const houseProfile = this.sessionStateService.getHouse().houseProfile;
      // Consumption
      if (houseProfile === "Consumption" || houseProfile === "Storage") {
        if (
          Array.from(this.smartMeterIDs.keys()).includes(message.moduleLocalId)
        ) {
          this.smartMeterIDs.set(message.moduleLocalId, message.value);
          this.gridConsumption = 0;
          this.smartMeterIDs.forEach((value: number, key: string) => {
            this.gridConsumption = this.gridConsumption + value;
          });
        }
      }
      // Micro
      if (houseProfile === "Microgeneration") {
        // Consumption
        if (
          Array.from(this.consumptionMeterIDs.keys()).includes(
            message.moduleLocalId
          )
        ) {
          this.consumptionMeterIDs.set(message.moduleLocalId, message.value);
          this.gridConsumption = 0;
          this.consumptionMeterIDs.forEach((value: number, key: string) => {
            this.gridConsumption = this.gridConsumption + value;
          });
        }
      }
      if (houseProfile === "Selfconsumption") {
        if (
          Array.from(this.smartMeterIDs.keys()).includes(message.moduleLocalId)
        ) {
          this.smartMeterIDs.set(message.moduleLocalId, message.value);
          this.gridConsumption = 0;
          this.smartMeterIDs.forEach((value: number, key: string) => {
            this.gridConsumption = this.gridConsumption + value;
          });
        }
      }
    }
  }

  private applyingInjectionValues(message: any): void {
    const platformKey = message.platformKey;
    if (platformKey === STATE_VAR.ACTIVE_POWER_A_MINUS) {
      if (
        Array.from(this.productionMeterIDs.keys()).includes(
          message.moduleLocalId
        )
      ) {
        this.productionMeterIDs.set(message.moduleLocalId, message.value);
        this.gridInjection = 0;
        this.productionMeterIDs.forEach((value: number, key: string) => {
          this.gridInjection = this.gridInjection + value;
        });
      }
    }
  }

  private applyingProductionValues(message: any): void {
    const keyMsg = message.platformKey;
    this.productionValues.set(message.moduleLocalId, message.value);
    let totalValue = 0;
    this.productionValues.forEach((value, key, map) => (totalValue += value));
    this.selfConsumption =
      totalValue - this.gridInjection >= 0
        ? totalValue - this.gridInjection
        : 0;
  }

  ngOnDestroy() {
    if (this.realTimeSubscription) {
      this.realTimeSubscription.unsubscribe();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/home/home-tab/home-tab.component.ts